import React from 'react';
import { Body5, Container } from '@ui';
import { FooterSeparator } from '@ui';
import { FlatButton } from '@ui/buttons';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { FormState } from '@ui/FormState';
import { Working } from '@ui/Working';
import { ROUTES } from '@constants/routes';
import { sendApiDownloadRequest } from '@/services/api';
import { logNetRequest } from '@/utils';
import { ModeContext } from '@/contexts/ModeContext';
import styled from 'styled-components';
import { RfpTemplateResultsContext } from './RfpTemplateResultsContext';
import { RfpTemplateAnswers } from './RfpTemplateAnswers';

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  height: 80px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  width: 200px;
`;

export function ResultsPhase() {
  const { modeType } = React.useContext(ModeContext);
  const { isComplete, resultId, templateRunId, templateId, selectedTemplate, sourceDocIds, isStreamingEnabled } =
    React.useContext(RfpTemplateResultsContext);
  const [formError, setFormError] = React.useState(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const hasSourceDocs = sourceDocIds.length > 0;

  const onDownloadReport = async () => {
    setIsDownloading(true);
    try {
      const filename = 'GeneratedReport';
      const fillResultId = templateRunId ? 0 : resultId;
      let URL = `v1/genai/templates/filled/${modeType}/${fillResultId}?template_id=${templateId}`;
      if (templateRunId) {
        URL = `v1/genai/templates/filled/${modeType}/${fillResultId}?template_id=${templateId}&template_run_id=${templateRunId}`;
      }
      const response = await sendApiDownloadRequest(URL);
      const { status, data, error } = response;
      logNetRequest(status, data, URL);
      if (status === 200) {
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setFormError(error || 'Something went wrong downloading the file, please try again later.');
      }
    } catch (error) {
      setFormError(error || 'Something went wrong downloading the file, please try again later.');
      console.error('Error downloading the file:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleQueryDocuments = React.useCallback(() => {
    if (!hasSourceDocs) {
      return;
    }

    const url = ROUTES.digitalAnalyst + `?doc-ids=${sourceDocIds.join(',')}`;
    window.open(url, '_blank');
  }, [hasSourceDocs, sourceDocIds]);

  const isButtonDisabled = !isComplete || isDownloading;
  const buttonLabel = isDownloading ? 'Downloading...' : 'Download Results';

  return (
    <Container top={24}>
      <ContentWrapper>
        {isComplete ? (
          <Body5 bold>Document Generation Complete</Body5>
        ) : (
          <Working message="Generating report" noVerticalPadding />
        )}
      </ContentWrapper>
      <Container top={16}>
        <FooterSeparator />
      </Container>
      <Container top={0} row spaceBetween verticallyCenter>
        <ButtonWrapper>
          <FlatButton disabled={isButtonDisabled} onClick={isButtonDisabled ? () => null : onDownloadReport} fullWidth>
            {buttonLabel}
          </FlatButton>
        </ButtonWrapper>
        <Container>
          <SkeletonButton disabled={!hasSourceDocs} onClick={handleQueryDocuments}>
            Query Documents
          </SkeletonButton>
        </Container>
      </Container>
      {!isComplete && (
        <Container top={8}>
          <Body5 isLowContrast>Download available when document generation is complete.</Body5>
        </Container>
      )}
      <Container top={16}>
        <FormState error={formError} />
        {isStreamingEnabled && <RfpTemplateAnswers templateId={templateId} template={selectedTemplate} />}
      </Container>
    </Container>
  );
}
